// src/hooks/useKeyboardOpen.ts
import { useEffect, useRef, useState } from "react";

type KeyboardState = {
  isOpen: boolean;
  height: number; // 추정 키보드 높이(px), 레이아웃 보정용
};

export function useKeyboardOpen(thresholdRatio = 0.82): KeyboardState {
  const [state, setState] = useState<KeyboardState>({
    isOpen: false,
    height: 0,
  });
  const baseline = useRef<number>(0); // 기준 높이
  const raf = useRef<number | null>(null);
  const focused = useRef(false);

  useEffect(() => {
    const vv = window.visualViewport;

    // 소프트 키보드가 있는 터치 기기에서만 포커스 보정을 적용한다.
    // (데스크톱 웹에서는 입력창 포커스만으로 키보드가 열리지 않으므로 제외)
    const hasSoftKeyboard =
      typeof window !== "undefined" &&
      (window.matchMedia?.("(pointer: coarse)").matches ||
        "ontouchstart" in window);

    const measure = () => {
      const viewportH = vv?.height ?? window.innerHeight;

      if (!baseline.current) {
        // 키보드가 내려간 상태에서의 기준값(최대 높이) 추정
        baseline.current = Math.max(viewportH, baseline.current || 0);
      }

      // iOS: visualViewport.height가 줄어듦
      // Android: innerHeight가 줄어듦(여기선 viewportH로 통일)
      const ratio = viewportH / (baseline.current || viewportH);
      // 포커스 보정은 소프트 키보드가 있는 기기에서만
      const open =
        ratio < thresholdRatio || (hasSoftKeyboard && focused.current);
      const kbHeight = Math.max(0, (baseline.current || viewportH) - viewportH);

      setState({ isOpen: open, height: open ? kbHeight : 0 });
    };

    const schedule = () => {
      if (raf.current) cancelAnimationFrame(raf.current);
      raf.current = requestAnimationFrame(measure);
    };

    // 초기 측정
    schedule();

    // iOS
    vv?.addEventListener("resize", schedule);
    // Android/기타
    window.addEventListener("resize", schedule);

    // 포커스 보정(일부 단말에서 resize없이 포커스만 바뀌는 케이스)
    const onFocusIn = (e: Event) => {
      const t = e.target as HTMLElement | null;
      focused.current =
        !!t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.getAttribute("contenteditable") === "true");
      schedule();
    };
    const onFocusOut = () => {
      focused.current = false;
      // 약간 딜레이 후 닫힘 측정(키보드 내려가는 애니메이션 보정)
      setTimeout(schedule, 50);
    };

    window.addEventListener("focusin", onFocusIn);
    window.addEventListener("focusout", onFocusOut);

    return () => {
      vv?.removeEventListener("resize", schedule);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("focusin", onFocusIn);
      window.removeEventListener("focusout", onFocusOut);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [thresholdRatio]);

  return state;
}
